class IndexedDBHelper {
  private dbName: string;
  private dbVersion: number;
  private storeName: string;

  constructor(dbName: string, storeName: string, dbVersion: number = 1) {
    this.dbName = dbName;
    this.storeName = storeName;
    this.dbVersion = dbVersion;
  }

  public async openDatabase(): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id' });
        }
      };

      request.onsuccess = (event) => {
        resolve((event.target as IDBOpenDBRequest).result);
      };

      request.onerror = (event) => {
        reject((event.target as IDBOpenDBRequest).error);
      };
    });
  }

  public async storeData<T>(id: string, data: T): Promise<void> {
    const db = await this.openDatabase();
    const transaction = db.transaction(this.storeName, 'readwrite');
    const store = transaction.objectStore(this.storeName);

    const dataToStore = { id, data };
    const request = store.put(dataToStore);

    request.onsuccess = () => {
      // console.log(`Data stored successfully with id: ${id}`);
    };

    request.onerror = (error) => {
      console.error(`Error storing data: ${JSON.stringify(error)}`);
    };
  }

  public async storeFileData(id: string, file: File): Promise<void> {
    await this.storeData(id, file);
  }

  public async storeStringData(id: string, data: string): Promise<void> {
    await this.storeData(id, data);
  }

  public async storeArrayData<S>(id: string, data: S): Promise<void> {
    await this.storeData(id, data);
  }

  public async getData<R>(id: string): Promise<R | null> {
    const db = await this.openDatabase();
    const transaction = db.transaction(this.storeName, 'readonly');
    const store = transaction.objectStore(this.storeName);

    return new Promise<R | null>((resolve) => {
      const request = store.get(id);
      request.onsuccess = (event) => {
        const result = (event.target as IDBRequest).result;
        resolve(result ? result.data : null);
      };
      request.onerror = (error) => {
        console.error(`Error retrieving data: ${JSON.stringify(error)}`);
        resolve(null);
      };
    });
  }

  // 删除单个数据
  public async deleteData(id: string): Promise<void> {
    const db = await this.openDatabase();
    const transaction = db.transaction(this.storeName, 'readwrite');
    const store = transaction.objectStore(this.storeName);

    const request = store.delete(id);

    request.onsuccess = () => {
      // console.log(`Data with id: ${id} deleted successfully.`);
    };

    request.onerror = (error) => {
      console.error(`Error deleting data: ${JSON.stringify(error)}`);
    };
  }

  // 清空所有数据
  public async clearAllData(): Promise<void> {
    const db = await this.openDatabase();
    const transaction = db.transaction(this.storeName, 'readwrite');
    const store = transaction.objectStore(this.storeName);

    // 使用 clear() 方法清空所有数据
    const request = store.clear();

    return new Promise<void>((resolve, reject) => {
      request.onsuccess = () => {
        console.log(`All data cleared from ${this.storeName}`);
        resolve();
      };
      request.onerror = (error) => {
        console.error(`Error clearing data: ${JSON.stringify(error)}`);
        reject(error);
      };
    });
  }
}

export default IndexedDBHelper;
